/**
 * Envio de NF do técnico (magic-link, sem login).
 * Lista os serviços do mês + total a receber, e permite upload da Nota Fiscal.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Loader2, Receipt } from "lucide-react";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { publicNfApi, type PublicNfInvoice } from "@/lib/api-public";
import { formatCurrency, formatDate } from "@/lib/format";

const MONTHS = [
  "", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const SUBMITTED = new Set(["reviewing", "scheduled", "paid"]);

export function NfSubmitPublicPage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const q = useQuery({
    queryKey: ["public-nf", token],
    queryFn: () => publicNfApi.get(token),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("no-file");
      const { upload_url, storage_path } = await publicNfApi.uploadUrl(token, file.name, file.type);
      await publicNfApi.putFile(upload_url, file);
      return publicNfApi.submit(token, file.name, storage_path);
    },
    onSuccess: (data) => {
      qc.setQueryData(["public-nf", token], data);
      toast.success("NF enviada! Obrigado.");
    },
    onError: () => toast.error("Não foi possível enviar a NF", { description: "Tente novamente." }),
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Link inválido ou expirado</h1>
          <p className="mt-2 text-slate-500">Solicite um novo link ao time EuNerd.</p>
        </div>
      </div>
    );
  }

  const inv: PublicNfInvoice = q.data;
  const periodo = inv.period_month ? `${MONTHS[inv.period_month]}/${inv.period_year}` : "";
  const done = SUBMITTED.has(inv.status) || !!inv.nf_submitted_at;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-sky-500 text-white">
            <Receipt className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight">Seus atendimentos {periodo}</h1>
            <p className="text-sm text-slate-500">{inv.nerd_name} · {inv.code}</p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-slate-500">Total a receber</span>
            <span className="text-2xl font-bold text-emerald-600">{formatCurrency(inv.total)}</span>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {inv.items.map((it, idx) => (
            <div key={idx} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
              <div className="min-w-0">
                <div className="truncate font-medium">{it.description}</div>
                <div className="text-xs text-slate-500">{it.job_code}</div>
              </div>
              <span className="font-semibold tabular-nums whitespace-nowrap">{formatCurrency(it.total)}</span>
            </div>
          ))}
        </div>

        {done ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800">
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="h-5 w-5" /> NF recebida
            </div>
            <p className="mt-1 text-sm">
              Sua nota fiscal{inv.nf_filename ? ` (${inv.nf_filename})` : ""} foi enviada e está em
              análise. Após a aprovação, o pagamento é agendado.
            </p>
          </div>
        ) : (
          <div className="sticky bottom-0 mt-6 rounded-2xl border border-slate-200 bg-white/90 p-4 backdrop-blur">
            {inv.nf_deadline && (
              <p className="mb-3 text-sm text-slate-600">
                Envie sua <b>Nota Fiscal</b> até <b>{formatDate(inv.nf_deadline)}</b>.
              </p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" className="flex-1" onClick={() => fileRef.current?.click()}>
                <FileUp className="h-4 w-4" />
                {file ? file.name : "Escolher arquivo (PDF)"}
              </Button>
              <Button
                onClick={() => submit.mutate()}
                disabled={!file || submit.isPending}
                className="bg-sky-600 hover:bg-sky-700"
              >
                {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                Enviar NF
              </Button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">EuNerd · Pagamentos</p>
      </div>
    </div>
  );
}
